import { getEnumKeyByEnumValue } from '../../helpers/enum';
import {
    combineApproval,
    combineFeedback,
    combineSettings,
    combineShift,
    combineTrackAndApproval,
    combineUser,
    formatEmergencyContact,
    formatHoursBlock,
    handleCreatedUser,
    handleDeletedShift,
} from '../../helpers/transformers';
import { FormOption } from '../../types';
import { EventLogCategory } from '../EventLogCategory/EventLogCategory';
import {
    EventLog,
    EventLogActionType,
    EventLogDataItem,
    EventLogDataItemBodyResource,
    EventLogEntityTypeResource,
    EventLogResource,
    EventLogWeeksHours,
} from './EventLog';

const transformEventLogDateToLocalDate = (rawDate: string): Date => {
    const date = new Date(rawDate);
    const dateWithoutOffset = date.getTime() - (date.getTimezoneOffset() * 60_000);

    return new Date(dateWithoutOffset);
};

const logOrder = ['start', 'startTime', 'end', 'endTime'];
const orderMap = new Map(logOrder.map((key, i) => [key, i]));
const orderIndex = (key: string) => orderMap.get(key) ?? Number.MAX_SAFE_INTEGER;

export const transformToEventLogDataItem = (
    id: string,
    type: EventLogEntityTypeResource,
    action: EventLogActionType,
    body: EventLogDataItemBodyResource,
): EventLogDataItem => {
    const entries = Object.entries(body);
    const filteredEntries = entries
        .filter(([key]) => !['_summary', '_entity', 'EventTracker'].includes(key))
        .map(([key, value]) => ([key, Object.fromEntries(
            Object.entries(value)
                .filter(([, v]) => v)
                .sort((prev, next) => orderIndex(prev[0]) - orderIndex(next[0])),
        )]));
    let data = Object.fromEntries(filteredEntries);

    if (body.LeaveOfAbsenceHour) {
        data.LeaveOfAbsenceHour = formatHoursBlock(body.LeaveOfAbsenceHour as EventLogWeeksHours);
    }

    if (body.AbsenceHour) {
        data.AbsenceHour = formatHoursBlock(body.AbsenceHour as EventLogWeeksHours);
    }

    const createdUserResult = handleCreatedUser(body.Person, action, id, type);
    if (createdUserResult) {
        return createdUserResult;
    }

    const deletedShiftResult = handleDeletedShift(body.Shift, action, id, type, body);
    if (deletedShiftResult) {
        return deletedShiftResult;
    }

    if (typeof body.Shift === 'object') {
        data = {
            ...data,
            ...combineShift(body.Shift),
        };
    }

    if (typeof body.Track === 'object') {
        data = {
            ...data,
            ...combineTrackAndApproval(data.Track, data.Approval),
        };
    }

    if (typeof body.Approval === 'object') {
        data = {
            ...data,
            ...combineApproval(body.Approval),
        };
    }

    if (typeof body.Feedback === 'object') {
        data = {
            ...data,
            ...combineFeedback(body.Feedback),
        };
    }

    if (typeof body.Setting === 'object') {
        data = {
            ...data,
            ...combineSettings(body.Setting),
        };
    }

    if (typeof body.User === 'object') {
        data = {
            ...data,
            ...combineUser(body.User),
        };
    }

    if (typeof body.EmergencyContact === 'object') {
        data = {
            ...data,
            ...formatEmergencyContact(body.EmergencyContact),
        };
    }

    return {
        id,
        data,
        type,
    };
};

export const transformToEventLog = (resource: EventLogResource): EventLog => {
    const {
        entityUuid,
        entityType,
        action,
        body,
        category,
        title,
    } = resource.data;

    const entityTypeKey = getEnumKeyByEnumValue(EventLogEntityTypeResource, entityType);
    const actionTypeKey = getEnumKeyByEnumValue(EventLogActionType, action);

    if (!entityTypeKey) {
        console.error(`[transformToEventLog]: Unknown enum found named "${entityType}".`);
    }

    if (!actionTypeKey) {
        console.error(`[transformToEventLog]: Unknown enum found named "${action}".`);
    }

    const type = entityTypeKey
        ? EventLogEntityTypeResource[entityTypeKey]
        : EventLogEntityTypeResource.other;

    const actionType = actionTypeKey
        ? EventLogActionType[actionTypeKey]
        : EventLogActionType.created;

    const logDataItem = transformToEventLogDataItem(
        entityUuid,
        type,
        action,
        body,
    );

    const name = body._entity?.fullName
        ? body._entity.fullName
        : '';

    const createdAt = transformEventLogDateToLocalDate(resource.createdAt);
    const updatedAt = transformEventLogDateToLocalDate(resource.updatedAt);

    return {
        id: resource.uuid,
        entityId: entityUuid,
        action: actionType,
        type,
        name,
        categorySlug: category,
        message: title,
        items: [logDataItem],
        createdBy: resource.createdBy,
        createdAt,
        updatedAt,
    };
};

export const transformEventLogCategoryToFormOption = (eventLogCategory: EventLogCategory): FormOption => ({
    label: eventLogCategory.name,
    value: eventLogCategory.slug,
});

export const sortByUpdatedAtDesc = (items: EventLog[]): EventLog[] => [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
);
